/**
 * Button Component
 * 
 * @description
 * Reusable button component with multiple variants for iGaming platform.
 * Supports primary, secondary, outline, ghost, and danger variants.
 * 
 * @module components/ui/Button
 */

import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

export type ButtonVariant = 
  | 'primary'    // Neon cyan glow
  | 'secondary'  // Muted style
  | 'outline'   // Bordered
  | 'ghost'      // Transparent
  | 'danger'     // Error red
  | 'gold';      // Premium gold accent

export type ButtonSize = 
  | 'sm' 
  | 'md' 
  | 'lg';

/**
 * Button Props
 */
export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Visual style variant */
  variant?: ButtonVariant;
  /** Size of the button */
  size?: ButtonSize;
  /** Loading state - shows spinner and disables interaction */
  loading?: boolean;
  /** Full width button */
  fullWidth?: boolean;
}

/**
 * Button Component
 * 
 * @example
 * ```tsx
 * <Button variant="primary" size="md" onClick={handleClick}>
 *   Submit
 * </Button>
 * ```
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'primary',
      size = 'md',
      loading = false,
      fullWidth = false,
      disabled,
      children,
      ...props
    },
    ref
  ) => {
    /**
     * Base styles applied to all buttons
     */
    const baseStyles = cn(
      'inline-flex items-center justify-center gap-2 font-medium rounded-lg',
      'transition-base focus-ring',
      'disabled:opacity-50 disabled:cursor-not-allowed'
    );

    /**
     * Variant-specific styles
     */
    const variants = {
      primary: cn(
        'bg-neon-cyan text-background',
        'hover:shadow-neon hover:bg-neon-cyan/90',
        'active:bg-neon-cyan/80'
      ),
      secondary: cn(
        'bg-background-tertiary text-foreground',
        'hover:bg-background-tertiary/80 hover:text-foreground',
        'active:bg-background-tertiary/70'
      ),
      outline: cn(
        'border border-border text-foreground-muted',
        'hover:border-border-hover hover:text-foreground hover:bg-background-tertiary',
        'active:bg-background-secondary'
      ),
      ghost: cn(
        'text-foreground-muted',
        'hover:text-foreground hover:bg-background-tertiary',
        'active:bg-background-secondary'
      ),
      danger: cn(
        'bg-error text-white',
        'hover:shadow-[0_0_20px_hsl(0_75%_55%_/_0.4)] hover:bg-error/90',
        'active:bg-error/80'
      ),
      gold: cn(
        'bg-gold text-background font-semibold',
        'hover:shadow-gold hover:bg-gold-light',
        'active:bg-gold-dark'
      ),
    };

    /**
     * Size-specific styles
     */
    const sizes = {
      sm: 'h-8 px-3 text-sm',
      md: 'h-10 px-4 text-sm',
      lg: 'h-12 px-6 text-base',
    };

    return (
      <button
        ref={ref}
        className={cn(
          baseStyles,
          variants[variant],
          sizes[size],
          fullWidth && 'w-full',
          className
        )}
        disabled={disabled || loading}
        {...props}
      >
        {loading && (
          /**
           * Loading spinner SVG
           * Simple animated spinner using SVG
           */
          <svg
            className="animate-spin h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        )}
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';